/**
 * Runtime palette for the storefront.
 *
 * The default palette lives in CSS (`:root` in src/globals.css) and every
 * Tailwind color name resolves to one of those variables, so nothing needs to
 * be applied at boot. `applyPalette` exists for the merchant-branding case: it
 * overwrites only the tokens it is given and leaves the rest at their default.
 *
 * Two entry points, on purpose:
 * - {@link applyPalette} takes the five BRAND tokens, the vocabulary this file
 *   was born with.
 * - {@link applyStorefrontColors} takes the ROLES the merchant actually
 *   edits in the portal (`StorefrontColors`) and lands them on the store's CSS:
 *   seven solid roles plus the two optional title band roles, which are
 *   resolved against the card before they reach a variable.
 *   The roles are not the tokens — `cardBackground` has no brand equivalent,
 *   and `background`/`text` land on gray-scale tokens — which is exactly why
 *   the translation lives here, next to the variables it writes, instead of
 *   being spread across the components that read them.
 */
import {
  resolveTitleCardColors,
  type StorefrontColorRole,
  type StorefrontColors,
  type StorefrontTitleCardColorRole,
} from "@/domain/storefront-appearance";

export type ThemePalette = {
  primary: string;
  secondary: string;
  success: string;
  danger: string;
  accent: string;
};

export const DEFAULT_PALETTE: ThemePalette = {
  primary: "#E5770B",
  secondary: "#15803D",
  success: "#0EAB14",
  danger: "#DB1B0D",
  accent: "#F36A15",
};

type Rgb = [number, number, number];

/** Text color used over a light brand color; matches `gray-scale-900`. */
const DARK_FOREGROUND: Rgb = [37, 37, 37];
const LIGHT_FOREGROUND: Rgb = [255, 255, 255];

/** Tokens whose contrast color is computed and written alongside them. */
const FOREGROUND_TOKENS: Partial<Record<keyof ThemePalette, string>> = {
  primary: "--color-primary-foreground",
  secondary: "--color-secondary-foreground",
  danger: "--color-danger-foreground",
};

const CSS_VARIABLES: Record<keyof ThemePalette, string> = {
  primary: "--color-primary",
  secondary: "--color-secondary",
  success: "--color-success",
  danger: "--color-danger",
  accent: "--color-accent",
};

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Parses `#E5770B`, `E5770B` or `#E70` into RGB channels.
 * Returns null for anything else so a bad value from an API or a config file
 * degrades to the default palette instead of writing garbage into the DOM.
 */
export function parseHexColor(value: string): Rgb | null {
  const match = HEX_PATTERN.exec(value.trim());
  if (!match) return null;

  const digits = match[1];
  const full =
    digits.length === 3
      ? digits
          .split("")
          .map((digit) => digit + digit)
          .join("")
      : digits;

  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

/** WCAG 2.1 relative luminance. */
function relativeLuminance([r, g, b]: Rgb): number {
  const channels = [r, g, b].map((channel) => {
    const ratio = channel / 255;
    return ratio <= 0.03928 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function contrastRatio(a: Rgb, b: Rgb): number {
  const [lighter, darker] = [relativeLuminance(a), relativeLuminance(b)].sort(
    (x, y) => y - x,
  );
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * WCAG AA threshold for large/bold text, which is what solid CTAs are.
 * Chosen over "whichever contrasts most": maximum contrast would put dark
 * labels on the default orange (5.09 against white's 3.01) and flip the whole
 * app away from the brand's white-on-orange. White stays the intent; a merchant
 * who picks a pale color is the case worth correcting.
 */
const LIGHT_FOREGROUND_MIN_CONTRAST = 3;

/**
 * Picks the label color for a background: white unless it would be unreadable,
 * then near-black. A merchant who picks `#FFE600` gets dark labels instead of
 * the invisible white-on-yellow that a hardcoded `text-white` would produce.
 */
export function pickForeground(background: Rgb): Rgb {
  return contrastRatio(background, LIGHT_FOREGROUND) >=
    LIGHT_FOREGROUND_MIN_CONTRAST
    ? LIGHT_FOREGROUND
    : DARK_FOREGROUND;
}

function toTriplet([r, g, b]: Rgb): string {
  return `${r} ${g} ${b}`;
}

/**
 * Writes one color to its variable, plus the computed readable text color when
 * that variable has one. A value that is missing or not a hex color is SKIPPED,
 * never written as garbage: the token keeps whatever globals.css declared, so a
 * single bad entry costs one color instead of the page.
 */
function writeColor(
  root: HTMLElement,
  variable: string,
  value: string | undefined,
  foregroundVariable?: string,
): void {
  if (!value) return;

  const rgb = parseHexColor(value);
  if (!rgb) return;

  root.style.setProperty(variable, toTriplet(rgb));

  if (foregroundVariable) {
    root.style.setProperty(foregroundVariable, toTriplet(pickForeground(rgb)));
  }
}

/**
 * Writes the palette to CSS variables on `root` (defaults to `<html>`).
 * Invalid or missing entries are skipped, so a partial palette is valid and
 * every unset token keeps the default declared in globals.css.
 */
export function applyPalette(
  palette: Partial<ThemePalette>,
  root: HTMLElement = document.documentElement,
): void {
  for (const key of Object.keys(CSS_VARIABLES) as (keyof ThemePalette)[]) {
    writeColor(root, CSS_VARIABLES[key], palette[key], FOREGROUND_TOKENS[key]);
  }
}

/**
 * WHERE EACH STOREFRONT ROLE LANDS IN CSS.
 *
 * Merchants pick seven ROLES ("store background", "action color", "border"),
 * not seven tokens. This map is the only place that speaks both languages.
 *
 * `primary` and `secondary` reuse existing tokens (action and price). The other
 * four get their OWN tokens so the role has a name instead of a gray step.
 *
 * `background` writes TWO places: storewide `gray-100` is the quiet neutral
 * surface (button hover, missing-photo tile, progress track, disabled field) —
 * "background one shade up". On a dark theme a fixed `gray-100` becomes a white
 * blotch. `gray-900` stays untouched: modal veil (`SCRIM`) and dark
 * badges — tying it to the palette would wash out dark stores.
 */
const STOREFRONT_COLOR_VARIABLES: Record<StorefrontColorRole, string[]> = {
  background: ["--color-page", "--color-gray-100"],
  primary: ["--color-primary"],
  secondary: ["--color-secondary"],
  cardBackground: ["--color-card"],
  border: ["--color-border"],
  cardText: ["--color-card-text"],
  text: ["--color-page-text"],
};

/**
 * Title band has its own token; it does not read `--color-card` directly.
 *
 * Until the merchant pins a color, the value written HERE is the card's (the
 * contract resolves `null` to the card role before CSS). The separate token
 * lets the band change later without every card changing, while `TitleBand`
 * still reads one variable.
 */
const TITLE_CARD_VARIABLES: Record<StorefrontTitleCardColorRole, string> = {
  titleCardBackground: "--color-title-card",
  titleCardText: "--color-title-card-text",
};

/**
 * Roles whose readable text color is COMPUTED and written beside them.
 *
 * Only the two that become solid label backgrounds: "Adicionar" (`primary`) and
 * the discount price badge (`secondary`). A light-yellow action gets dark text
 * instead of invisible white. `cardBackground` is excluded on purpose: card
 * text is the chosen `cardText` role, not a derived value.
 */
const STOREFRONT_FOREGROUND_TOKENS: Partial<
  Record<StorefrontColorRole, string>
> = {
  primary: "--color-primary-foreground",
  secondary: "--color-secondary-foreground",
};

/**
 * Paints the storefront with roles chosen in the portal.
 *
 * Applies storewide — menu, bag, checkout, orders — not home only: the customer
 * crosses that boundary constantly, and a bag that snaps back to white looks
 * broken.
 */
export function applyStorefrontColors(
  colors: StorefrontColors,
  root: HTMLElement = document.documentElement,
): void {
  for (const role of Object.keys(
    STOREFRONT_COLOR_VARIABLES,
  ) as StorefrontColorRole[]) {
    STOREFRONT_COLOR_VARIABLES[role].forEach((variable, index) =>
      writeColor(
        root,
        variable,
        colors[role],
        index === 0 ? STOREFRONT_FOREGROUND_TOKENS[role] : undefined,
      ),
    );
  }

  const titleCard = resolveTitleCardColors(colors);
  for (const role of Object.keys(
    TITLE_CARD_VARIABLES,
  ) as StorefrontTitleCardColorRole[]) {
    writeColor(root, TITLE_CARD_VARIABLES[role], titleCard[role]);
  }
}

/** Drops every runtime override, falling back to the palette in globals.css. */
export function resetPalette(
  root: HTMLElement = document.documentElement,
): void {
  for (const variable of Object.values(CSS_VARIABLES)) {
    root.style.removeProperty(variable);
  }
  for (const variable of Object.values(FOREGROUND_TOKENS)) {
    if (variable) root.style.removeProperty(variable);
  }
}
